import { useAnalyticsFilters } from "../../../context/AnalyticsFilterContext";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  ScatterChart,
  Scatter,
} from "recharts";
import { getWeeklyExceptionData } from "../../../services/apiServices";
import FolderFilter from "../../common/FolderFilter";

const WeeklyExceptionReport = () => {
  const [chartData, setChartData] = useState([]);
  const [normative, setNormative] = useState(null);
  const [weekRange, setWeekRange] = useState("");
  const [loading, setLoading] = useState(true); 
  const { selectedFolder } = useAnalyticsFilters();

  useEffect(() => {
    const fetchWeeklyExceptionData = async () => {
      try {
        setLoading(true);
        const loggedInUserDetails = JSON.parse(localStorage.getItem("loggedInUserDetails"));
        const today = new Date();
        const daysToMonday = (today.getDay() || 7) - 1 + 7;
        const prevMonday = new Date(today);
        prevMonday.setDate(today.getDate() - daysToMonday);
        prevMonday.setHours(0, 0, 0, 0);

        const prevSunday = new Date(prevMonday);
        prevSunday.setDate(prevMonday.getDate() + 6);
        prevSunday.setHours(23, 59, 59, 999);

        const formatDate = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        const startDate = formatDate(prevMonday);
        const endDate = formatDate(prevSunday);

        setWeekRange(`${startDate} to ${endDate}`);
        const weeklyData = await getWeeklyExceptionData(
          loggedInUserDetails,
          startDate,
          endDate,
          selectedFolder
        );
        if (!Array.isArray(weeklyData?.data?.data)) {
          setChartData([]);
          return;
        }

        const enriched = weeklyData.data?.data.map((dev) => {
          const loc = dev.locAdjusted;
          const hours = Number(dev.totalHoursWorked) + Number(dev.totalHoursWorkedInCall)
          const totalHours = hours ? hours : Number(dev?.hrmsWorkHours)
          const productivity = totalHours > 0 ? loc / totalHours : 0;
          return { ...dev, productivity, loc, totalHours };
        });

        setNormative(weeklyData?.data?.normative);
        const threshold = weeklyData?.data?.normative * 0.75;

        const finalChartData = enriched.map((dev) => ({
          developer: dev.developer || "Unknown",
          productivity: Number(dev.productivity.toFixed(2)),
          belowThreshold: dev.productivity < threshold,
          totalLOC: dev.loc,
          locAdjusted : dev.locAdjusted,
          totalHoursWorked: dev?.totalHours,
          isRunning: dev.isRunning
        }));
        setChartData(finalChartData);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyExceptionData();
  }, [selectedFolder]);

  if (loading) {
    return (
      <div
        style={{
          margin: "30px",
          padding: "20px",
          borderRadius: "8px",
          backgroundColor: "#f9f9f9",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <div className="loader" style={{
          border: "6px solid #f3f3f3",
          borderTop: "6px solid #3498db",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          animation: "spin 1s linear infinite",
          marginBottom: "15px"
        }} />
        <p style={{ fontSize: "16px", color: "#333" }}>Loading, please wait...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // render your original charts
  const renderCustomBarLabel = ({ x, y, width, value }) => {
    if (!value) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        fill="#000"
        fontSize={12}
        fontWeight="bold"
      >
        {value}
      </text>
    );
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
            borderRadius: "5px",
          }}
        >
          <p><strong>{label}</strong></p>
          <p>Productivity: {data?.productivity} LOC/hour</p>
          <p>LOC Adjusted: {data?.locAdjusted}</p>
          <p>Total Hours Worked: {data?.totalHoursWorked?.toFixed(2)}</p>
        </div>
      );
    }
    return null;
  };
  const customTick = ({ x, y, payload }) => {
    const devData = chartData.find(d => d.developer === payload.value);
    const isRunning = devData ? devData.isRunning : true;

    return (
      <g transform={`translate(${x}, ${y})`}>
        <text
          textAnchor="end"
          transform="rotate(-35)"  // rotate in place
          fontSize={12}
          fill="#666"
          dy={12}                  // move slightly down
        >
          {payload.value}
          {!isRunning && <tspan fill="red"> *</tspan>}
        </text>
      </g>
    );
  };


  const barSize = 20;
  const totalBarSpace = chartData.length * barSize;
  const chartMinWidth = totalBarSpace + chartData.length * 20;

  return (
    <div
      style={{
        border: "1px solid #ccc",
        margin: "30px",
        padding: "14px",
        borderRadius: "8px",
        backgroundColor: "#f9f9f9",
        textAlign: "center",
        overflowX: "auto",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        {/* Left empty space (keeps title centered) */}
        <div></div>

        {/* Center: Title + Week */}
        <div style={{ textAlign: "center" }}>
          <h4 style={{ margin: "0 0 6px 0" }}>
            Weekly Exception Report
          </h4>
          <p style={{ margin: 0, fontSize: "14px", color: "#555" }}>
            <strong>Week:</strong> {weekRange}
          </p >
          {normative && (
        <p style={{ marginTop: 10, fontSize: "14px", color: "#555" }}>
          <strong>Normative Value:</strong> {normative.toFixed(2)} LOC/hour
        </p>
      )}
        </div>

        {/* Right: Select Folder + Dropdown */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <span style={{ fontSize: "16px", color: "#555" }}>
            Select Folder:
          </span>
          <FolderFilter />
        </div>
      </div>

    
    
  <hr style={{ border: "none", borderTop: "1px solid #ddd", marginBottom: "20px" }} />

      {/* Scatter Chart */}
      <div style={{ width: "100%", height: "400px", marginTop: "40px" }}>

        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid />
            <XAxis
              type="category"
              dataKey="developer"
              interval={0}
              height={70}
              tick={({ x, y, payload }) => {
                const devData = chartData.find(d => d.developer === payload.value);
                const isRunning = devData ? devData.isRunning : true;

                return (
                  <g transform={`translate(${x}, ${y + 10})`}>
                    <text
                      textAnchor="end"
                      transform="rotate(-35)"
                      fontSize={12}
                      fill="#666"
                    >
                      {payload.value}
                      {!isRunning && <tspan fill="red"> *</tspan>}
                    </text>
                  </g>
                );
              }}
            />

            <YAxis
              type="number"
              dataKey="productivity"
              label={{
                value: "Productivity (LOC/hour)",
                angle: -90,
                position: "insideLeft",
                style: {
                  fontSize: "100%",
                  fill: "black",
                  textAnchor: "middle"
                }
              }}
            />

            {normative && (
              <ReferenceLine
                y={normative}
                stroke="blue"
                strokeDasharray="3 3"
                label={{ value: "Normative Factor", position: "insideTop", fill: "blue" }}
              />
            )}
            <Tooltip cursor={{ strokeDasharray: "3 3" }} />
            <Scatter
              data={chartData}
              fill="#2979ff"
              shape={(props) => {
                const { cx, cy, payload } = props;
                const color = payload.belowThreshold ? "#f44336" : "#2979ff";
                return <circle cx={cx} cy={cy} r={6} fill={color} />;
              }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Bar Chart */}
      <div style={{ minWidth: `${chartMinWidth}px`, height: "450px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
            barCategoryGap={chartData.length < 6 ? 80 : 20}
            barGap={2}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="developer" interval={0} height={70} tick={customTick}>
              <Label
                value="Developers"
                position="insideBottom"
                offset={-18}
                style={{ fill: "black" }}
              />
            </XAxis>
            <YAxis>
              <Label
                angle={270}
                position="insideLeft"
                value="Productivity (LOC/hour)"
                style={{ fontSize: "100%", fill: "black", textAnchor: "insideTop" }}
              />
            </YAxis>
            {normative && (
              <ReferenceLine y={normative} stroke="blue" strokeDasharray="3 3" label={{ value: "Normative Factor", position: "insideTop", fill: "blue" }} />
            )}
            <Tooltip content={customTooltip} />
            <Bar
              dataKey="productivity"
              fill="#82ca9d"
              label={renderCustomBarLabel}
              barSize={barSize}
              fillOpacity={0.9}
              shape={(props) => {
                const { x, y, width, height, payload } = props;
                const barColor = payload.belowThreshold ? "#f44336" : "#4caf50";
                return <rect x={x} y={y} width={width} height={height} fill={barColor} />;
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default WeeklyExceptionReport;
